'use client'

import { type ReactNode, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuthContext } from '../contexts/AuthContext'
import { useLocale, useT } from '../contexts/LocaleContext'
import { useTelegramBotAPI } from '../hooks/useTelegramBotAPI'
import { walletApi } from '../api/endpoints'
import LuckyIcon from '../components/lucky/LuckyIcon'
import CoinGlyph from '../components/lucky/CoinGlyph'
import { GuessGlyph, SupportGlyph, TrophyGlyph } from '../components/lucky/ProfileGlyphs'
import SocialLinks from '../components/layout/SocialLinks'
import { COIN_TYPES } from '../types'
import { coinNameKey, formatNumber } from '../utils/helpers'
import { LOCALE_META } from '../i18n'

type Tile = {
  key: string
  titleKey: string
  subtitleKey: string
  path: string
  icon: ReactNode
}

export default function HomePage() {
  const navigate = useNavigate()
  const t = useT()
  const { locale } = useLocale()
  const numberLocale = LOCALE_META[locale].numberLocale
  const { isAuthenticated, isLoading, user } = useAuthContext()
  const { user: tgUser } = useTelegramBotAPI()

  const { data: walletData } = useQuery({
    queryKey: ['wallet'],
    queryFn: () => walletApi.getCoins(),
    enabled: isAuthenticated,
  })
  const coins = walletData?.coins || {
    bronze: 0,
    silver: 0,
    gold: 0,
    diamond: 0,
  }

  const tiles: Tile[] = useMemo(
    () => [
      {
        key: 'airdrop',
        titleKey: 'home.tiles.airdrop.title',
        subtitleKey: 'home.tiles.airdrop.subtitle',
        path: '/airdrop',
        icon: <LuckyIcon name="gift" size={36} alt="" />,
      },
      {
        key: 'guess',
        titleKey: 'home.tiles.guess.title',
        subtitleKey: 'home.tiles.guess.subtitle',
        path: '/games/guess-number/play',
        icon: <GuessGlyph size={36} />,
      },
      {
        key: 'football',
        titleKey: 'home.tiles.football.title',
        subtitleKey: 'home.tiles.football.subtitle',
        path: '/football',
        icon: <LuckyIcon name="world" size={36} alt="" />,
      },
      {
        key: 'horse',
        titleKey: 'home.tiles.horse.title',
        subtitleKey: 'home.tiles.horse.subtitle',
        path: '/horse',
        icon: <TrophyGlyph size={36} />,
      },
      {
        key: 'gift',
        titleKey: 'home.tiles.gift.title',
        subtitleKey: 'home.tiles.gift.subtitle',
        path: '/gift-card',
        icon: <LuckyIcon name="chest" size={36} alt="" />,
      },
      {
        key: 'wallet',
        titleKey: 'home.tiles.wallet.title',
        subtitleKey: 'home.tiles.wallet.subtitle',
        path: '/wallet',
        icon: <LuckyIcon name="wallet" size={36} alt="" />,
      },
      {
        key: 'cash',
        titleKey: 'home.tiles.cash.title',
        subtitleKey: 'home.tiles.cash.subtitle',
        path: '/cash-out',
        icon: <LuckyIcon name="coin" size={36} alt="" />,
      },
      {
        key: 'profile',
        titleKey: 'home.tiles.profile.title',
        subtitleKey: 'home.tiles.profile.subtitle',
        path: '/profile',
        icon: <LuckyIcon name="profile" size={36} alt="" />,
      },
      {
        key: 'support',
        titleKey: 'home.tiles.support.title',
        subtitleKey: 'home.tiles.support.subtitle',
        path: '/support',
        icon: <SupportGlyph size={36} />,
      },
    ],
    [],
  )

  if (isLoading || !isAuthenticated) {
    return null
  }

  const firstName = user?.first_name || tgUser?.first_name || t('common.friend')
  const photo = user?.photo_url || tgUser?.photo_url

  return (
    <div className="lucky-home mini-app-container pb-20">
      <div className="lucky-home__inner">
        <header className="lucky-home__top">
          <div className="lucky-home__who">
            {photo ? (
              <img src={photo} alt="" className="lucky-home__avatar" />
            ) : (
              <div className="lucky-home__avatar lucky-home__avatar--fallback">{firstName[0]}</div>
            )}
            <div className="min-w-0">
              <p className="lucky-home__hello">{t('home.hello', { name: firstName })}</p>
              <h1 className="lucky-home__brand">Sisil</h1>
            </div>
          </div>
        </header>

        <div className="lucky-home__coins">
          {COIN_TYPES.map((type) => (
            <button
              key={type}
              type="button"
              className="lucky-home__coin"
              onClick={() => navigate('/wallet')}
            >
              <CoinGlyph type={type} size={20} />
              <strong>{formatNumber(coins[type] ?? 0, numberLocale)}</strong>
              <span>{t(coinNameKey(type))}</span>
            </button>
          ))}
        </div>

        <button
          type="button"
          className="lucky-home__banner"
          onClick={() => navigate('/games/lucky-numbers/play')}
        >
          <div className="lucky-home__banner-icon">
            <LuckyIcon name="logo" size={44} alt="" className="lucky-home__logo" />
          </div>
          <div className="lucky-home__banner-text">
            <h2>{t('home.luckyTitle')}</h2>
            <p>{t('home.luckySub')}</p>
          </div>
          <span className="lucky-home__banner-cta">{t('common.play')}</span>
        </button>

        <div className="lucky-home__section-head">
          <h3>{t('home.quickStart')}</h3>
          <button type="button" onClick={() => navigate('/games')}>
            {t('home.allGames')}
          </button>
        </div>

        <div className="lucky-home__grid">
          {tiles.map((tile) => {
            const clearBg =
              tile.key === 'football' ||
              tile.key === 'gift' ||
              tile.key === 'wallet' ||
              tile.key === 'profile'
            return (
              <button
                key={tile.key}
                type="button"
                className="lucky-home__tile"
                onClick={() => navigate(tile.path)}
              >
                <span className={`lucky-home__tile-icon${clearBg ? ' lucky-home__tile-icon--clear' : ''}`}>
                  {tile.icon}
                </span>
                <span className="lucky-home__tile-text">
                  <strong>{t(tile.titleKey)}</strong>
                  <span>{t(tile.subtitleKey)}</span>
                </span>
              </button>
            )
          })}
        </div>

        <SocialLinks />
      </div>
    </div>
  )
}
